import { z } from 'zod';
import { ActionTarget, BanRequest } from './action.js';
import { IpAddress, IsoTime, ServerId, Severity } from './basics.js';
/**
 * The AI layer's settings and outputs (knowledge doc sections 13, 15, 16 and 20;
 * P9.2).
 *
 * Section 13 makes AI advisory only, and these shapes are where that is held:
 * every model output has a schema here, checked before anything reads it, and no
 * output names an action. A **provider** is where a model is reached; a **route**
 * says which provider and model a task uses and what to fall back to; the
 * **settings** are section 13's `enabled`, `daily_token_budget` and `redact`.
 * Model names are settings, not code.
 *
 * Provider keys are write-only: a request may carry one, no response does.
 */
/**
 * Where a model is reached. Section 20 began with Anthropic and Ollama; P11.3
 * (2026-10-05, at the user's request) adds every first-party provider the Vercel
 * AI SDK ships, the Vercel AI Gateway — one key, many vendors' models — Azure
 * OpenAI, and any service that speaks OpenAI's API (OpenRouter, Together,
 * Fireworks, LM Studio, vLLM). Ollama stays its own kind: a local model, unmetered.
 */
export const AiProviderKind = z.enum([
    'anthropic',
    'openai',
    'google',
    'mistral',
    'groq',
    'xai',
    'deepseek',
    'gateway',
    'azure',
    'openai_compatible',
    'ollama',
]);
/**
 * Each kind's facts, shared so the API's checks, the model factory and the form
 * say the same thing. An address given for a first-party kind is a proxy in front
 * of it (a company gateway): the vendor's own path is still added after it.
 */
export const AI_PROVIDER_KINDS = {
    anthropic: {
        label: 'Anthropic',
        needsKey: true,
        needsAddress: false,
        origin: 'https://api.anthropic.com',
        path: '/v1',
        exampleModel: 'claude-sonnet-4-5',
        meteredByDefault: true,
        hint: 'A key from console.anthropic.com.',
    },
    openai: {
        label: 'OpenAI',
        needsKey: true,
        needsAddress: false,
        origin: 'https://api.openai.com',
        path: '/v1',
        exampleModel: 'gpt-5-mini',
        meteredByDefault: true,
        hint: 'A key from platform.openai.com.',
    },
    google: {
        label: 'Google Gemini',
        needsKey: true,
        needsAddress: false,
        origin: 'https://generativelanguage.googleapis.com',
        path: '/v1beta',
        exampleModel: 'gemini-2.5-flash',
        meteredByDefault: true,
        hint: 'A Gemini API key from aistudio.google.com.',
    },
    mistral: {
        label: 'Mistral',
        needsKey: true,
        needsAddress: false,
        origin: 'https://api.mistral.ai',
        path: '/v1',
        exampleModel: 'mistral-medium-latest',
        meteredByDefault: true,
        hint: 'A key from console.mistral.ai.',
    },
    groq: {
        label: 'Groq',
        needsKey: true,
        needsAddress: false,
        origin: 'https://api.groq.com',
        path: '/openai/v1',
        exampleModel: 'llama-3.3-70b-versatile',
        meteredByDefault: true,
        hint: 'A key from console.groq.com.',
    },
    xai: {
        label: 'xAI (Grok)',
        needsKey: true,
        needsAddress: false,
        origin: 'https://api.x.ai',
        path: '/v1',
        exampleModel: 'grok-4',
        meteredByDefault: true,
        hint: 'A key from console.x.ai.',
    },
    deepseek: {
        label: 'DeepSeek',
        needsKey: true,
        needsAddress: false,
        origin: 'https://api.deepseek.com',
        path: '',
        exampleModel: 'deepseek-chat',
        meteredByDefault: true,
        hint: 'A key from platform.deepseek.com.',
    },
    gateway: {
        label: 'Vercel AI Gateway',
        needsKey: true,
        needsAddress: false,
        origin: 'https://ai-gateway.vercel.sh',
        path: '/v4/ai',
        exampleModel: 'anthropic/claude-sonnet-4.5',
        meteredByDefault: true,
        hint: 'One key from vercel.com for every vendor it carries; name models as vendor/model.',
    },
    azure: {
        label: 'Azure OpenAI',
        needsKey: true,
        needsAddress: true,
        origin: null,
        path: '/openai',
        exampleModel: 'my-gpt-deployment',
        meteredByDefault: true,
        hint: 'The resource address, https://<resource>.openai.azure.com; the model is your deployment’s name.',
    },
    openai_compatible: {
        label: 'OpenAI-compatible service',
        needsKey: false,
        needsAddress: true,
        origin: null,
        path: '/v1',
        exampleModel: 'meta-llama/llama-3.3-70b-instruct',
        meteredByDefault: true,
        hint: 'OpenRouter, Together, Fireworks, LM Studio, vLLM… its address without /v1, and a key if it asks for one.',
    },
    ollama: {
        label: 'Ollama',
        needsKey: false,
        needsAddress: true,
        origin: null,
        path: '/v1',
        exampleModel: 'llama3.2',
        meteredByDefault: false,
        hint: 'A model on your own machine, for example http://10.0.0.5:11434.',
    },
};
/**
 * Addresses for well-known OpenAI-compatible services, so nobody has to look them
 * up. Each is the address without `/v1`, which the API adds.
 */
export const OPENAI_COMPATIBLE_PRESETS = [
    { label: 'OpenRouter', baseUrl: 'https://openrouter.ai/api' },
    { label: 'Together AI', baseUrl: 'https://api.together.xyz' },
    { label: 'Fireworks AI', baseUrl: 'https://api.fireworks.ai/inference' },
    { label: 'Perplexity', baseUrl: 'https://api.perplexity.ai' },
    { label: 'LM Studio on this network', baseUrl: 'http://127.0.0.1:1234' },
    { label: 'vLLM on this network', baseUrl: 'http://127.0.0.1:8000' },
];
/**
 * What a model is asked to do. Section 13's routing example names
 * `summarize_events` and `incident_analysis`; `explain_event` and `ask` are two
 * more of its uses. P9.2 uses `explain_event`; P9.3 the rest.
 */
export const AiTask = z.enum(['explain_event', 'summarize_events', 'incident_analysis', 'ask']);
/** `default` serves every task that has no route of its own. */
export const AiRouteTask = z.enum(['default', ...AiTask.options]);
/** A provider's test is recorded with the rest, so it shows in usage and counts. */
export const AiCallTask = z.enum(['test', ...AiTask.options]);
/**
 * Section 13: "Mask or remove IPs, usernames and paths where configured", and its
 * example's `redact: [usernames, internal_hostnames]`. A redacted value becomes a
 * placeholder (`[ip-1]`), the same one each time within a request, so the model can
 * still say "the same address did both".
 */
export const AiRedaction = z.enum(['ips', 'usernames', 'internal_hostnames', 'paths']);
/** Section 13's example, as the starting point. */
export const AI_DEFAULT_REDACTION = ['usernames', 'internal_hostnames'];
/** Section 13's example figure. */
export const AI_DEFAULT_DAILY_TOKEN_BUDGET = 200_000;
export const AI_MAX_DAILY_TOKEN_BUDGET = 100_000_000;
/** Lower-case letters, digits and dashes: it appears in the usage history and the audit log. */
export const AiProviderName = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,39}$/, 'Lower-case letters, digits and dashes, up to 40');
/**
 * A model as the provider names it: `claude-sonnet-4-5`, `llama3.2:3b`,
 * `hf.co/org/model:Q4_K_M`. Only characters model names use.
 */
export const AiModelName = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:/@+-]*$/, 'A model name as the provider writes it');
const ipv4Parts = (host) => {
    const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
    if (match === null)
        return undefined;
    const parts = match.slice(1).map(Number);
    return parts.every((part) => part <= 255) ? parts : undefined;
};
/**
 * This machine, or an address that does not route on the internet: loopback,
 * RFC 1918, the shared 100.64.0.0/10 that VPN overlays use, and IPv6's unique-local
 * and link-local ranges. Literal addresses only: a name could resolve anywhere.
 */
export function isPrivateOrLoopbackHost(hostname) {
    const host = hostname.toLowerCase().replace(/^\[(.*)\]$/, '$1');
    if (host === 'localhost')
        return true;
    const v4 = ipv4Parts(host);
    if (v4 !== undefined) {
        const [a = 0, b = 0] = v4;
        return (a === 127 ||
            a === 10 ||
            (a === 172 && b >= 16 && b <= 31) ||
            (a === 192 && b === 168) ||
            (a === 100 && b >= 64 && b <= 127));
    }
    if (!host.includes(':'))
        return false;
    return host === '::1' || /^f[cd][0-9a-f]{0,2}:/.test(host) || /^fe[89ab][0-9a-f]?:/.test(host);
}
/**
 * Why a provider address is refused, or `undefined`. HTTPS, or plain HTTP to this
 * machine or a private address (section 13's `http://10.0.0.5:11434`): event data
 * must not cross the internet in the clear. No credentials, query or fragment in
 * it, and no `/v1`, which the API adds itself.
 */
export function aiBaseUrlProblem(value) {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return 'Not an address (for example https://api.example.com or http://10.0.0.5:11434)';
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:')
        return 'Only http and https';
    if (url.username !== '' || url.password !== '') {
        return 'No user name or password in the address; the key goes in its own field';
    }
    if (url.search !== '' || url.hash !== '')
        return 'No query or fragment in the address';
    if (/\/v1\/?$/.test(url.pathname))
        return 'Leave out /v1; it is added for you';
    if (url.protocol === 'http:' && !isPrivateOrLoopbackHost(url.hostname)) {
        return 'Plain http only to this machine or a private address; use https';
    }
    return undefined;
}
export const AiBaseUrl = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .superRefine((value, ctx) => {
    const problem = aiBaseUrlProblem(value);
    if (problem !== undefined)
        ctx.addIssue({ code: 'custom', message: problem });
});
/** Printable, no spaces: it travels in an HTTP header. */
export const AiApiKey = z
    .string()
    .min(1)
    .max(500)
    .regex(/^[\x21-\x7e]+$/, 'A key has no spaces or unusual characters');
/**
 * `POST /ai/providers` and `PUT /ai/providers/:id`. The key is write-only: left
 * out, the stored one stays; `null` clears it; a string replaces it. A kind that
 * `needsKey` must have one stored (the API says so); one that `needsAddress` must
 * be given its address.
 */
export const AiProviderInput = z
    .object({
    name: AiProviderName,
    kind: AiProviderKind,
    /** `null` for a first-party kind means the vendor's own address (`AI_PROVIDER_KINDS`). */
    baseUrl: AiBaseUrl.nullable(),
    apiKey: AiApiKey.nullable().optional(),
    /** Whether this provider's tokens count against the daily budget. */
    metered: z.boolean(),
    enabled: z.boolean().default(true),
})
    .strict()
    .superRefine((input, ctx) => {
    const info = AI_PROVIDER_KINDS[input.kind];
    if (info.needsAddress && input.baseUrl === null) {
        ctx.addIssue({
            code: 'custom',
            path: ['baseUrl'],
            message: `${info.label} needs its address. ${info.hint}`,
        });
    }
});
export const AiProviderView = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: AiProviderKind,
    baseUrl: z.string().nullable(),
    hasKey: z.boolean(),
    metered: z.boolean(),
    enabled: z.boolean(),
    createdAt: IsoTime,
    updatedAt: IsoTime,
    /** The routes that name it, as primary or fallback: it cannot be deleted while any do. */
    usedBy: z.array(AiRouteTask),
});
export const AiModelChoice = z.object({ providerId: z.uuid(), model: AiModelName });
/** Section 13's `routing` entry. The fallback is tried when the first choice fails. */
export const AiRoute = z
    .object({
    task: AiRouteTask,
    providerId: z.uuid(),
    model: AiModelName,
    fallback: AiModelChoice.nullable(),
})
    .strict()
    .refine((route) => route.fallback?.providerId !== route.providerId || route.fallback.model !== route.model, { message: 'The fallback is the same model as the first choice', path: ['fallback'] });
/** `PUT /ai/routing`: the whole table, each task at most once. */
export const AiRoutingRequest = z
    .object({ routes: z.array(AiRoute).max(AiRouteTask.options.length) })
    .strict()
    .refine((body) => new Set(body.routes.map((route) => route.task)).size === body.routes.length, {
    message: 'Each task at most once',
    path: ['routes'],
});
export const AiSettingsInput = z
    .object({
    enabled: z.boolean(),
    /** Tokens a day across metered providers. 0 leaves only unmetered (local) ones. */
    dailyTokenBudget: z.number().int().min(0).max(AI_MAX_DAILY_TOKEN_BUDGET),
    redact: z
        .array(AiRedaction)
        .max(AiRedaction.options.length)
        .refine((list) => new Set(list).size === list.length, 'Each at most once'),
    /**
     * Section 13's suggestions made on their own as attacks arrive (P9.3), not only
     * when someone asks. Off by default: it spends the budget unasked.
     */
    autoSuggest: z.boolean().default(false),
})
    .strict();
export const AiSettingsView = AiSettingsInput.extend({
    updatedAt: IsoTime,
    updatedByEmail: z.string().nullable(),
    /** False when the API has no encryption key: no provider key can be stored or opened. */
    encryptionAvailable: z.boolean(),
});
/** What anyone signed in may know: whether a task can be asked for, never where it goes. */
export const AiStatus = z.object({
    enabled: z.boolean(),
    tasks: z.record(AiTask, z.boolean()),
});
export const AiCallStatus = z.enum(['running', 'ok', 'failed', 'refused']);
export const AiCall = z.object({
    id: z.uuid(),
    task: AiCallTask,
    providerName: z.string(),
    model: z.string(),
    metered: z.boolean(),
    fallback: z.boolean(),
    inputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
    status: AiCallStatus,
    /** What went wrong, as the provider or the check said it: never a key, never the prompt. */
    error: z.string().nullable(),
    requestedByEmail: z.string().nullable(),
    createdAt: IsoTime,
    finishedAt: IsoTime.nullable(),
});
export const AiUsageDay = z.object({
    /** `YYYY-MM-DD`, UTC: the budget's day. */
    day: z.string(),
    meteredTokens: z.number().int(),
    unmeteredTokens: z.number().int(),
    calls: z.number().int(),
});
export const AiUsage = z.object({
    day: z.string(),
    /** Metered tokens spent and reserved today. */
    used: z.number().int(),
    budget: z.number().int(),
    days: z.array(AiUsageDay),
    recent: z.array(AiCall),
});
export const AiProviderTestRequest = z.object({ model: AiModelName }).strict();
export const AiProviderTestResult = z.object({
    ok: z.literal(true),
    inputTokens: z.number().int().nullable(),
    outputTokens: z.number().int().nullable(),
    ms: z.number().int(),
});
/**
 * The model's explanation of one event (section 13: "explain an event in plain
 * language; suggest priority"). Text to read, and nothing else: no field names an
 * action, so nothing can act on it. Every field is required because structured
 * output on both providers asks for all of them; the limits are generous so a
 * model that writes a little long is not a failure, and the screen shows text only.
 */
export const EventExplanation = z
    .object({
    summary: z.string().min(1).max(2000),
    likelyCause: z.string().max(2000),
    /** Whether the reported severity seems right for what the event shows. */
    severity: z.enum(['agree', 'lower', 'higher']),
    severityReason: z.string().max(1000),
    nextSteps: z.array(z.string().min(1).max(500)).max(8),
    confidence: z.enum(['low', 'medium', 'high']),
})
    .strict();
export const EventExplanationView = z.object({
    eventId: z.uuid(),
    explanation: EventExplanation,
    providerName: z.string(),
    model: z.string(),
    /** What was masked before it was sent. */
    redacted: z.array(AiRedaction),
    createdAt: IsoTime,
    createdByEmail: z.string().nullable(),
});
/**
 * The model's reading of an incident (`incident_analysis`; P9.3). It recommends —
 * ban, watch, or nothing to do — and for a ban says for how long and why. It never
 * names the address: that is the incident's, chosen by Bartizo from the data, so
 * text an attacker wrote into a log cannot point a ban at someone else.
 */
export const IncidentAnalysis = z
    .object({
    title: z.string().min(1).max(160),
    summary: z.string().min(1).max(2500),
    priority: Severity,
    recommendation: z.enum(['ban', 'watch', 'ignore']),
    /** Only for a ban; between an hour and thirty days. */
    banHours: z.number().int().min(1).max(720).nullable(),
    /** CrowdSec shows it as the decision's reason. One short line. */
    reason: z.string().max(100),
})
    .strict();
/** What a suggestion proposes: a ban, as `POST /actions` would take it. */
export const ProposedBan = z
    .object({
    type: z.literal('ban_ip'),
    target: ActionTarget,
    ip: IpAddress,
    duration_seconds: z.number().int(),
    reason: z.string(),
})
    .strict();
export const AiSuggestionStatus = z.enum(['proposed', 'approved', 'rejected', 'expired', 'closed']);
export const AiSuggestion = z.object({
    id: z.uuid(),
    kind: z.literal('incident'),
    subjectIp: z.string().nullable(),
    title: z.string(),
    summary: z.string(),
    priority: Severity,
    recommendation: z.enum(['ban', 'watch', 'ignore']),
    proposedAction: ProposedBan.nullable(),
    relatedEventIds: z.array(z.uuid()),
    /** The servers the related events came from, for the approval dialog's target. */
    servers: z.array(z.object({ id: ServerId, name: z.string() })),
    providerName: z.string(),
    model: z.string(),
    status: AiSuggestionStatus,
    origin: z.enum(['automatic', 'requested']),
    requestedByEmail: z.string().nullable(),
    reviewedByEmail: z.string().nullable(),
    reviewedAt: IsoTime.nullable(),
    reviewNote: z.string().nullable(),
    actionId: z.uuid().nullable(),
    createdAt: IsoTime,
    expiresAt: IsoTime,
});
export const AiSuggestionListQuery = z.object({
    /** `open` is what waits for a person: proposed and not expired. */
    status: z.union([AiSuggestionStatus, z.literal('open')]).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * `POST /ai/suggestions/:id/approve`: the ban as the person confirms it. The
 * address is the suggestion's and cannot be changed here; the target, the
 * duration and the reason can, as in the ban dialog.
 */
export const ApproveSuggestionRequest = BanRequest.omit({ ip: true })
    .extend({ target: ActionTarget })
    .strict();
export const RejectSuggestionRequest = z
    .object({ note: z.string().trim().max(500).optional() })
    .strict();
/** `/ai/ask`'s windows: the last hour to the last week. */
export const ASK_HOURS = [1, 6, 24, 72, 168];
export const AskRequest = z
    .object({
    question: z
        .string()
        .trim()
        .min(3)
        .max(500)
        .regex(/^[^\p{Cc}]*$/u, 'One paragraph, without control characters'),
    serverId: ServerId.optional(),
    hours: z
        .number()
        .int()
        .refine((hours) => ASK_HOURS.includes(hours), 'Not one of the windows')
        .default(24),
})
    .strict();
/** The model's output for `ask`: an answer, and which of the events given it rests on. */
export const AskAnswer = z
    .object({
    answer: z.string().min(1).max(3000),
    citedRefs: z.array(z.string().regex(/^e\d{1,3}$/)).max(10),
})
    .strict();
export const AskCitedEvent = z.object({
    id: z.uuid(),
    severity: Severity,
    category: z.string(),
    serverName: z.string(),
    message: z.string(),
    occurredAt: IsoTime,
});
export const AskResponse = z.object({
    answer: z.string(),
    cited: z.array(AskCitedEvent),
    /** How many events were in the window, and how many of them the model was shown. */
    eventsInWindow: z.number().int(),
    eventsShown: z.number().int(),
    providerName: z.string(),
    model: z.string(),
    redacted: z.array(AiRedaction),
});
//# sourceMappingURL=ai.js.map